import { type ReactNode } from 'react';
import { Drawer as MantineDrawer, type DrawerProps as MantineDrawerProps } from '@mantine/core';

export interface ForgeDrawerProps
  extends Omit<MantineDrawerProps, 'children' | 'opened' | 'onClose'> {
  opened: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Side from which the drawer slides in. Defaults to `right`. */
  position?: 'left' | 'right' | 'top' | 'bottom';
  /** Footer slot — actions stick to the bottom of the panel. */
  footer?: ReactNode;
  children?: ReactNode;
}

/**
 * Drawer — side panel with focus trap, ESC close, and dark-glass backdrop.
 * Use for filter pickers, range detail inspectors, audit row drilldowns.
 */
export function Drawer({
  opened,
  onClose,
  title,
  position = 'right',
  size = 'md',
  footer,
  children,
  ...rest
}: ForgeDrawerProps) {
  return (
    <MantineDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      position={position}
      size={size}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 4,
        color: '#09090b',
      }}
      styles={{
        content: {
          backgroundColor: 'rgb(var(--c-deep))',
          borderInlineStart:
            position === 'right' ? '1px solid var(--c-bd)' : undefined,
          borderInlineEnd:
            position === 'left' ? '1px solid var(--c-bd)' : undefined,
          borderBlockStart:
            position === 'bottom' ? '1px solid var(--c-bd)' : undefined,
          borderBlockEnd: position === 'top' ? '1px solid var(--c-bd)' : undefined,
          display: 'flex',
          flexDirection: 'column',
        },
        header: {
          backgroundColor: 'rgb(var(--c-deep))',
          borderBottom: '1px solid var(--c-bd)',
          fontWeight: 600,
        },
        body: {
          flex: 1,
          padding: 20,
          overflowY: 'auto',
        },
      }}
      trapFocus
      closeOnEscape
      withCloseButton
      {...rest}
    >
      {children}
      {footer ? (
        <div
          style={{
            marginInline: -20,
            marginTop: 20,
            padding: 16,
            borderTop: '1px solid var(--c-bd)',
            backgroundColor: 'rgb(var(--c-deep))',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          {footer}
        </div>
      ) : null}
    </MantineDrawer>
  );
}
